import React from 'react';

import { FormattedMessage } from 'react-intl';

import { statFormatter } from '@nhl/formatter-utilities';
import { ReactTableEnums } from '@nhl/nhl-breakaway-ui';

import { puckAndPlayerTrackingSelectors } from '../../../../../redux/ducks/puckAndPlayerTracking';
import { commonMessages } from '../../../../../utility/commonMessages';
import { pptMessages } from '../../pptMessages';

const createColumnHeader = (intl, headerKey, headerTooltipKey) => (
    <span title={intl.formatMessage(headerTooltipKey)}>{intl.formatMessage(headerKey)}</span>
);

const createColumn = (id) => ({
    id,
    sortDescFirst: true,
    size: 100,
    meta: {
        headerStyle: {
            textAlign: 'center',
        },
        style: {
            textAlign: 'center',
        },
    },
});

export const columnDefs = (
    intl,
    speedConversionFactor,
    distanceConversionFactor,
    totalDistanceConversionFactor,
    conversionFactorKey,
    speedCeilingRandomizer,
) => {
    const isImperial = conversionFactorKey === puckAndPlayerTrackingSelectors.IMPERIAL_KEY;
    const distanceTooltip = isImperial
        ? pptMessages.measurement['distance.imp']
        : pptMessages.measurement['distance.met'];
    const distancePerShiftTooltip = isImperial
        ? pptMessages.measurement['distancePerShift.imp']
        : pptMessages.measurement['distancePerShift.met'];
    const speedTooltip = isImperial ? pptMessages.measurement['velocity.imp'] : pptMessages.measurement['velocity.met'];
    return [
        {
            id: 'player',
            cell: ({ row }) => `${row.original.Entity.JerseyNum} - ${row.original.Entity.NAbbrev}`,
            header: createColumnHeader(intl, pptMessages.header.player, pptMessages.tooltips.player),
            meta: {
                className: 'left-aligned paddedColumn',
                headerClassName: 'left-aligned paddedColumn',
                pinned: ReactTableEnums.Pinned.LEFT,
            },
            size: 200,
        },
        {
            ...createColumn('attackZonePercent'),
            cell: ({ row }) =>
                statFormatter.displayValueAs('ppPctg')(
                    row.original.ZoneOffenseTime / (row.original.ZoneOffenseTime + row.original.ZoneDefenseTime),
                ),
            header: createColumnHeader(intl, pptMessages.header.attackZonePercent, pptMessages.title.attackZonePercent),
        },
        {
            ...createColumn('distancePerShift'),
            cell: ({ row }) =>
                statFormatter.displayValueAs('goalsForPerGame')(
                    row.original.DistanceTotal / distanceConversionFactor / row.original.Stats?.Shifts,
                ) || null,
            header: createColumnHeader(intl, pptMessages.header.distancePerShift, distancePerShiftTooltip),
        },
        {
            ...createColumn('averageSpeed'),
            header: createColumnHeader(intl, pptMessages.header.speedAvg, speedTooltip),
            cell: ({ row }) => {
                const rawAvgSpeed = row.original.DistanceTotal / row.original.Stats?.TOITotal;
                // display avg speed as the smaller of two values, the computed average or the maximum speed minus the
                // ceiling randomizer. This avoids situations where, because of data being input at arenas, we have
                // skaters with impossibly high averages. This can happen if TOI data gets screwed up and entered as
                // artificially low. Eventually it will probably correct itself during the game.
                const avgSpeed = Math.min(rawAvgSpeed, row.original.SpeedMax - speedCeilingRandomizer);

                return statFormatter.displayValueAs('goalsForPerGame')(avgSpeed / speedConversionFactor);
            },
        },
        {
            ...createColumn('maxSpeed'),
            header: createColumnHeader(intl, pptMessages.header.speedMax, speedTooltip),
            cell: ({ row }) =>
                statFormatter.displayValueAs('goalsForPerGame')(row.original.SpeedMax / speedConversionFactor),
        },
        {
            ...createColumn('totalDistance'),
            header: createColumnHeader(intl, pptMessages.header.distance, distanceTooltip),
            cell: ({ row }) =>
                row.original.DistanceTotal ? (
                    statFormatter.displayValueAs('goalsForPerGame')(
                        row.original.DistanceTotal / totalDistanceConversionFactor,
                    )
                ) : (
                    <FormattedMessage {...commonMessages.misc.noInfo} />
                ),
        },
    ];
};
